import React from 'react';
import { DateTime } from 'luxon';
import { encodeDate, unpackToTime } from '@campfire/hot-date';
import { MyShiftItem } from '../MyShiftItem';
import { RosteredStatus, RosterTimelineItem, ActionType } from '../../ActivityTypes';
import { VolunteerRosters } from '../../ActivityQueries/__generated__/VolunteerRosters';
import { MyActivityStatusIndicator } from '../../../vol-dashboard/vol-dashboard-my-elements/main-section/my-activities/MyActivityStatusIndicator';
import { useUser } from '../../../../../global/auth/useUser';
import { MyActivityCICOActions } from '../../../vol-dashboard/vol-dashboard-my-elements/main-section/my-activities/CICO/MyActivityCICOActions';
import { LeavePeriodCard } from '../../../../../common/cards/leave-period-card/LeavePeriodCard';
import { useSharedAvailabilityCentreDialogState } from '../../../../../global/components/AvailabilityCenter/AvailabilityCenter';
import { useActivityCapacityStatus } from '../../../../../common/hooks/useActivityCapacityStatus';

interface Props {
  activities: RosterTimelineItem[];
  onAction: (action: ActionType, activity: RosterTimelineItem) => void;
  onSelect: (activityId: string, activityDate: string) => void;
  selectedActivityId?: string;
  selectedActivityDate?: string;
  getColor: (activityStatus: RosteredStatus) => any;
  volunteerRosterings?: VolunteerRosters[];
  getRosterActions: (activity: RosterTimelineItem) => ActionType[];
  onCICOAction: (activity: any, type: any, isAbleCheckOut: boolean) => void;
  vmAllowCICO: boolean;
  volunteerUnavailabilities?: Array<{
    volunteerUnavailabilityId: string;
    startDate: string;
    endDate: string;
  }>;
  volunteerCICOs?: Array<{
    CICOId: string;
    checkIn: any;
    checkOut: any | null;
    sessionReport: {
      session: { sessionId: string };
      activityReport: {
        activityDate: string;
        activity: { activityId: string };
      };
    };
  }>;
  refetch?: () => void;
  refetchListRosters?: () => void;
}

export function MyShiftTimeline({
  activities,
  onAction,
  onSelect,
  selectedActivityId,
  getColor,
  selectedActivityDate,
  volunteerRosterings,
  getRosterActions,
  onCICOAction,
  vmAllowCICO,
  volunteerUnavailabilities = [],
  volunteerCICOs = [],
  refetch,
  refetchListRosters,
}: Props) {
  const { getVolunteerIdentity, user } = useUser();
  const { volunteerId } = getVolunteerIdentity();
  const { configItems, userIdentityService } = user;
  const {
    setDialogOpen,
    setInitialOperation,
    setAdditionalRefetchFunctions,
  } = useSharedAvailabilityCentreDialogState();

  // Prepare activity requests for capacity status (only when config is enabled)
  const showCapacityStatus = configItems.vm.showCapacityStatus === true;
  const blockFullSessions =
    userIdentityService.privLevel === 'volunteer' && configItems.vm.blockFullSessionAvailability === true;
  const activityCapacityRequests = React.useMemo(() => {
    if (!showCapacityStatus && !blockFullSessions) return [];
    const grouped: Record<string, string[]> = {};
    activities.forEach((activity) => {
      if (!grouped[activity.activityId]) {
        grouped[activity.activityId] = [];
      }
      if (!grouped[activity.activityId].includes(activity.activityDate)) {
        grouped[activity.activityId].push(activity.activityDate);
      }
    });
    return Object.entries(grouped).map(([activityId, dates]) => ({ activityId, dates }));
  }, [activities, showCapacityStatus, blockFullSessions]);

  const { getStatusForActivityDate } = useActivityCapacityStatus(activityCapacityRequests);

  const isBlockedAsFull = (activity: { activityId: string; activityDate: string }) =>
    blockFullSessions && getStatusForActivityDate(activity.activityId, activity.activityDate)?.overallStatus === 'FULL';

  // Set up refetch functions for the AvailabilityCenter
  React.useEffect(() => {
    const refetchFunctions = [];
    if (refetch) refetchFunctions.push(refetch);
    if (refetchListRosters) refetchFunctions.push(refetchListRosters);
    setAdditionalRefetchFunctions(refetchFunctions);
  }, [refetch, refetchListRosters, setAdditionalRefetchFunctions]);

  const handleEditLeave = () => {
    setInitialOperation('unavailability');
    setDialogOpen(true);
  };

  // Build a combined list of activities and leave period indicators
  const itemsWithLeaveCards = React.useMemo(() => {
    const items: Array<{ type: 'activity' | 'leave'; data: any }> = [];

    // Add activities to the list
    activities.forEach((activity) => {
      items.push({ type: 'activity', data: activity });
    });

    // Add leave period start/end cards
    volunteerUnavailabilities.forEach((leave) => {
      // Add start card
      items.push({
        type: 'leave',
        data: {
          id: `leave-start-${leave.volunteerUnavailabilityId}`,
          date: leave.startDate,
          endDate: leave.endDate,
          leaveType: 'start',
        },
      });

      // Add end card (only if end date differs from start date)
      if (leave.startDate !== leave.endDate) {
        items.push({
          type: 'leave',
          data: {
            id: `leave-end-${leave.volunteerUnavailabilityId}`,
            date: leave.endDate,
            leaveType: 'end',
          },
        });
      }
    });

    // Sort by date
    items.sort((a, b) => {
      const dateA = a.type === 'activity' ? a.data.activityDate : a.data.date;
      const dateB = b.type === 'activity' ? b.data.activityDate : b.data.date;
      return DateTime.fromISO(dateA).toMillis() - DateTime.fromISO(dateB).toMillis();
    });

    return items;
  }, [activities, volunteerUnavailabilities]);

  return (
    <React.Fragment>
      {itemsWithLeaveCards.map((item) => {
        // Render leave period card
        if (item.type === 'leave') {
          return (
            <LeavePeriodCard
              key={item.data.id}
              type={item.data.leaveType}
              date={item.data.date}
              endDate={item.data.leaveType === 'start' ? item.data.endDate : undefined}
              onEdit={handleEditLeave}
            />
          );
        }

        // Render activity
        const activity = item.data;
        const nowDate = encodeDate(DateTime.local());
        const activeRosterings = activity.publishedRoster?.activeRosterings;
        const activeRostering = activeRosterings?.find(
          (rostering: any) => rostering.volunteer.volunteerId === volunteerId
        );
        const isAttended = activeRostering?.sessionRosterings.every((sessionRostering: any) =>
          volunteerCICOs.find(
            (cico) =>
              cico.checkIn &&
              cico.checkOut &&
              cico.sessionReport.activityReport.activityDate === activity.activityDate &&
              cico.sessionReport.activityReport.activity?.activityId === activity.activityId &&
              cico.sessionReport.session?.sessionId === sessionRostering.session.sessionId
          )
        );
        const checkInTimeRule =
          unpackToTime(activity.startTime)
            .diffNow()
            .as('hours') <= 6;
        const activityDate = activeRostering?.volunteer.checkedIn?.sessionReport.activityReport.activityDate;
        const checkedInActivityId =
          activeRostering?.volunteer.checkedIn?.sessionReport.activityReport.activity?.activityId;

        const isAbleCheckIn = nowDate === activity.activityDate && checkInTimeRule && activity.status === 'rostered';
        const isAbleCheckOut =
          activity.status === 'rostered' &&
          activityDate === activity.activityDate &&
          activity.activityId === checkedInActivityId &&
          !!activeRostering?.volunteer?.isCheckedIn;
        const ableCICO =
          (activity.allowCICO || activity.allowCICO === null) &&
          (activity.program.allowCICO || activity.program.allowCICO === null) &&
          vmAllowCICO &&
          !isAttended &&
          (isAbleCheckIn || isAbleCheckOut);

        const cicoConflict =
          activeRostering?.volunteer?.isCheckedIn &&
          (activityDate !== activity.activityDate || activity.activityId !== checkedInActivityId);

        // Check if this activity falls within any leave period
        const hasLeaveConflict =
          activity.status === 'rostered' &&
          volunteerUnavailabilities.some((leave) => {
            const activityDateTime = DateTime.fromISO(activity.activityDate);
            const leaveStart = DateTime.fromISO(leave.startDate);
            const leaveEnd = DateTime.fromISO(leave.endDate);
            return activityDateTime >= leaveStart && activityDateTime <= leaveEnd;
          });

        return (
          <MyShiftItem
            activity={activity}
            conflict={hasLeaveConflict}
            ActivityMenu={
              ableCICO ? (
                <MyActivityCICOActions
                  activity={activity}
                  onAction={(type) => onCICOAction(activity, type, isAbleCheckOut)}
                  cicoConflict={cicoConflict ?? false}
                  isCheckOut={isAbleCheckOut}
                />
              ) : (
                <MyActivityStatusIndicator
                  actions={getRosterActions(activity).filter(
                    (action) => !(action === 'available' && isBlockedAsFull(activity))
                  )}
                  onAction={(action) => onAction(action, activity)}
                  status={activity.status}
                />
              )
            }
            isSelected={selectedActivityId === activity.activityId && selectedActivityDate === activity.activityDate}
            onSelect={() => onSelect(activity.activityId, activity.activityDate)}
            color={getColor(activity.status)}
            rostering={volunteerRosterings?.find(
              (volunteerRostering) =>
                volunteerRostering.publishedRoster.activityDate === activity.activityDate &&
                volunteerRostering.publishedRoster.activity.activityId === activity.activityId
            )}
            capacityStatus={
              (showCapacityStatus || isBlockedAsFull(activity)) &&
              (activity.status === 'unsure' || activity.status === 'unavailable')
                ? getStatusForActivityDate(activity.activityId, activity.activityDate)
                : undefined
            }
          />
        );
      })}
    </React.Fragment>
  );
}
